import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Box, useTheme } from "@mui/material";
import cx from 'classnames';
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Text from "../../../../theme/text/Text";
import { setPrimaryFilter } from "../../../../../newapi/global/filter/reportFilterSlice";
import { tokens } from "../../../../../theme";
import { categoryAllBehaviors, reportFilterDataSource } from '../../chartFilterEnums';
import { useEffect, useState } from 'react';
import { useReportHubFilters } from '../../ReportHubFilterContext';
import { DropdownBaseV2 } from '../../../../theme/dropdowns/Dropdown';

export const CatogoryDropdown = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const filtersRegistry = useReportHubFilters();


    // resolve filter function
    const filter = filtersRegistry.filters[props.fKey];
    const resolvedFilter = typeof filter === "function" ? filter() : filter;


    const dispatch = useDispatch();
    const [filterData,setFilterData]= useState(null);
    
    const [runQuery, setRunQuery] = useState(false);
    const [filterDropdownOptionsSelected, setFilterDropdownOptionsSelected] = useState([]);
    
    const {data: queryData, isLoading, isFetching} = resolvedFilter.type === reportFilterDataSource.QUERY
        ? resolvedFilter.hook(resolvedFilter.hookArgs, { skip: !runQuery })
        : { data: null, isLoading: false, isFetching: false };

    // A filter can reshape what its source hands over before it is offered, like localizing a bucket
    // the backend only knows by its code.
    const applyMapData = (data) => resolvedFilter.mapData ? resolvedFilter.mapData(data) : data;

    const handleClick = () => {
        if (resolvedFilter.type === reportFilterDataSource.QUERY) {
            setRunQuery(true); 
        } else if (resolvedFilter.type === reportFilterDataSource.LOCAL) {
            const data = resolvedFilter.getData();
            setFilterData(applyMapData(data));
        }
    };

    useEffect(() => {
        if (resolvedFilter.type === reportFilterDataSource.QUERY) {
            if(queryData && !isLoading && !isFetching) {
                if(resolvedFilter.queryDataKey){
                    setFilterData(applyMapData(queryData[resolvedFilter.queryDataKey]));
                } else {
                    setFilterData(applyMapData(queryData));
                }
            }
        }
    },[queryData,resolvedFilter,isLoading,isFetching])

    // Options a filter always offers on top of whatever the select endpoint returns, like the 'manual'
    // bucket for orders without a channel.
    const options = [...(filterData || []), ...(resolvedFilter.dropdownOptions?.extraOptions ?? [])];

    const displayKey = resolvedFilter.dropdownOptions?.displayName ?? "name";
    const valueKey = resolvedFilter.dropdownOptions?.valueKey ?? "id";

    // An `all` category stores no values — the backend resolves them — so the ticks come from the
    // options themselves, otherwise reopening the dropdown shows nothing selected.
    let selectedValues = props.category.values?.map(item => item[valueKey]);
    if (props.category?.all) {
        selectedValues = options.map(option => option[valueKey]);
    }

    // With the flag set there are no stored values to count, but every option is ticked, so the count
    // is the list itself.
    let selectedCount = props.category?.values?.length ?? 0;
    if (props.category?.all) {
        selectedCount = options.length;
    }

    let selectionLabel = <Text noBreak>{t("no_selection")}</Text>;
    if (selectedCount > 0) {
        selectionLabel = (
            <>
                <Text noBreak bold>{selectedCount}</Text>
                <Text noBreak light sx={{ml:0.5,}}>
                    {t("selected")}
                </Text>
            </>
        );
    }

    return (
        <DropdownBaseV2
            options={options}
            selected={selectedValues}
            displayName={resolvedFilter.dropdownOptions?.displayName ?? "name"}
            valueKey={resolvedFilter.dropdownOptions?.valueKey ?? "id"}
            searchOptions={{
                enabled: true,
                placeholder: t("search")
            }}
            stopPropagation
            noPropagationOnSelect
            allowSelectAll
            multiple
            selectOnClose={(selected) => {
                // Picking every option is stored as the flag instead of this environment's ids, so the
                // report resolves to whatever channels the next relation has — and the backend fills in
                // the ones without orders as empty bars.
                if (options.length > 0 && selected.length === options.length) {
                    props.setCurrentReport((prev) => ({
                        ...prev,
                        category:{
                            ...prev?.category,
                            all: true,
                            // The filter decides what its own "all" means; the backend reads it from here.
                            allBehavior: resolvedFilter.allBehavior ?? categoryAllBehaviors.ZEROFILL,
                            values: [],
                        },
                    }));
                    return;
                }

                // Map selected indexes to objects with only displayName and valueKey
                // A saved value can be missing from the list, like a relation that has since gone inactive
                // or options that had not loaded yet. Keep the saved copy instead of crashing, which would
                // throw away the whole selection.
                const selectedItems = selected
                    .map((val) => options.find(fd => fd[valueKey] === val) ?? props.category.values?.find(v => v[valueKey] === val))
                    .filter(Boolean)
                    .map((item) => ({
                        [displayKey]: item[displayKey],
                        [valueKey]: item[valueKey],
                    }));

                // update currentReport
                props.setCurrentReport((prev) => ({
                    ...prev,
                    category:{
                        ...prev?.category,
                        all: false,
                        values: selectedItems,
                    },
                }));
            }}

        >
            {({anchorEl,onClick}) => (
                <Box
                    className={cx(undefined, {
                        'from-view':props.fromView,
                    })}
                    // key={"rp"+idx}
                    onClick={(e) => {
                        onClick(e);
                        handleClick();
                    }}
                    sx={{
                        px:2,
                        height:32,
                        borderRadius:4,
                        display:"flex",
                        alignItems:'center',
                        cursor:"pointer",
                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                        "&:hover .rp-value-container" : {
                            pr:4,
                        },
                        "&:hover .rp-value-container .rp-filter-icon" : {
                            zIndex:1,
                            opacity:1,
                                transition:"125ms",
                        },

                        '&.from-view' : {
                            height:24,
                        }
                    }}
                >
                    {selectionLabel}
                </Box>
            )}
        </DropdownBaseV2>
    )
}